import { Controller } from "@hotwired/stimulus"
import { ArcadeStage, arcadePalette } from "../utils/arcade_stage"

// Casse-brique : la raquette suit le pointeur, la balle démolit le mur, et le mur revient
// plus vite à chaque fois qu'on l'a vidé.
//
// Un seul <canvas> et une boucle `requestAnimationFrame` intégrée au temps réel (et non au
// nombre de frames) : sur un écran à 120 Hz, une boucle par frame ferait jouer la partie
// deux fois plus vite.
const COLUMNS = 9
const ROWS = 5
const LIVES = 3
const PADDLE_RATIO = 0.18       // largeur de la raquette, en fraction du plateau
const BALL_RATIO = 0.018
const START_SPEED = 0.62        // fraction de la hauteur parcourue par seconde
const SPEED_PER_WAVE = 0.09
// Le mur fait 45 briques : sans accélération DANS la vague, la balle gardait la même vitesse
// pendant des minutes et la partie ne se terminait que par lassitude. Chaque renvoi de
// raquette ajoute donc sa part — au bout d'un mur, la balle va environ un tiers plus vite.
const SPEED_PER_RETURN = 0.012
// Plafond : au-delà, la balle avance de plus d'une brique entre deux frames et traverse le
// mur sans le toucher (cf. le plafond de `delta` dans #loop).
const MAX_SPEED = 1.3
const PADDLE_Y = 0.9            // le plan que la balle doit franchir pour être renvoyée

export default class extends Controller {
  static values = { labels: Object, scoresUrl: String }

  start() {
    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
    })

    this.stage.open()
    // Les couleurs de la scène, que les skins du mode fun redonnent (cf. `.arcade-stage`).
    this.palette = arcadePalette(this.stage.root)
    this.#buildCanvas()
    this.#play()
  }

  #buildCanvas() {
    this.canvas = document.createElement("canvas")
    this.canvas.className = "arcade-breakout"
    this.stage.board.appendChild(this.canvas)
    // `ctx` et pas `context` : `context` appartient au contrôleur Stimulus.
    this.ctx = this.canvas.getContext("2d")

    this.onResize = () => this.#size()
    window.addEventListener("resize", this.onResize)
    this.#size()

    this.onPointer = (event) => this.#aim(event)
    this.canvas.addEventListener("pointermove", this.onPointer)
    this.canvas.addEventListener("pointerdown", this.onPointer)
  }

  #size() {
    const box = this.stage.board.getBoundingClientRect()
    const width = Math.max(260, Math.floor(Math.min(box.width, box.height * 1.4)) - 8)
    const height = Math.floor(width * 0.72)
    const ratio = window.devicePixelRatio || 1

    this.canvas.style.width = `${width}px`
    this.canvas.style.height = `${height}px`
    this.canvas.width = width * ratio
    this.canvas.height = height * ratio
    this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    this.width = width
    this.height = height
    this.#draw()
  }

  // --- partie -------------------------------------------------------------------------

  #play() {
    this.wave = 1
    this.#setLives(LIVES)
    this.stage.setScore(0)
    this.over = false
    this.#buildWall()
    this.#serve()
    this.stage.setHint(this.labelsValue.hint || "")
    this.last = null
    this.frame = window.requestAnimationFrame((time) => this.#loop(time))
  }

  #stop() {
    if (this.frame) window.cancelAnimationFrame(this.frame)
    this.frame = null
    window.removeEventListener("resize", this.onResize)
    this.canvas?.removeEventListener("pointermove", this.onPointer)
    this.canvas?.removeEventListener("pointerdown", this.onPointer)
    this.canvas = null
  }

  #buildWall() {
    this.bricks = []
    for (let row = 0; row < ROWS; row += 1) {
      for (let column = 0; column < COLUMNS; column += 1) {
        this.bricks.push({ row, column, alive: true })
      }
    }
  }

  // La balle repart du centre de la raquette, toujours vers le haut, avec un angle jamais
  // rasant : une trajectoire trop horizontale rebondit indéfiniment sans rien casser.
  #serve() {
    this.paddleX = this.paddleX ?? 0.5
    this.ballX = this.paddleX
    this.ballY = 0.86
    const angle = (Math.random() * 0.5 - 0.25) + -Math.PI / 2
    const speed = Math.min(START_SPEED + (this.wave - 1) * SPEED_PER_WAVE, MAX_SPEED)
    this.ballVX = Math.cos(angle) * speed
    this.ballVY = Math.sin(angle) * speed
    this.waiting = true
  }

  // Tout est en coordonnées relatives (0 → 1) : redimensionner la fenêtre en pleine partie
  // ne téléporte donc ni la balle ni la raquette.
  #aim(event) {
    const box = this.canvas.getBoundingClientRect()
    this.paddleX = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1)
    if (this.waiting) {
      this.ballX = this.paddleX
      this.waiting = false
    }
  }

  #loop(time) {
    if (this.over || !this.canvas) return

    const delta = this.last == null ? 0 : Math.min((time - this.last) / 1000, 0.05)
    this.last = time
    if (!this.waiting) this.#step(delta)
    this.#draw()
    this.frame = window.requestAnimationFrame((next) => this.#loop(next))
  }

  #step(delta) {
    // X et Y sont tous deux normalisés (0 → 1), mais sur des côtés de longueurs différentes :
    // sans ce facteur, la balle irait visiblement plus vite à l'horizontale qu'à la verticale.
    const ratio = this.height / this.width

    this.previousBallY = this.ballY
    this.ballX += this.ballVX * delta * ratio
    this.ballY += this.ballVY * delta

    const radius = BALL_RATIO
    if (this.ballX <= radius || this.ballX >= 1 - radius) {
      this.ballVX *= -1
      this.ballX = Math.min(Math.max(this.ballX, radius), 1 - radius)
    }
    if (this.ballY <= radius) {
      this.ballVY = Math.abs(this.ballVY)
      this.ballY = radius
    }

    this.#hitPaddle()
    this.#hitBricks()

    if (this.ballY > 1) this.#loseLife()
  }

  // L'angle de renvoi dépend du point d'impact : c'est ce qui rend la raquette pilotable
  // plutôt que subie.
  #hitPaddle() {
    const half = PADDLE_RATIO / 2
    // On teste la TRAVERSÉE du plan de la raquette, pas la présence dans une bande : une
    // bande fixe se fait traverser dès que la balle avance de plus que son épaisseur entre
    // deux frames, et la balle finit par passer à travers la raquette.
    if (this.ballVY < 0 || this.previousBallY > PADDLE_Y || this.ballY < PADDLE_Y) return
    if (Math.abs(this.ballX - this.paddleX) > half) return

    const offset = (this.ballX - this.paddleX) / half
    const speed = Math.min(Math.hypot(this.ballVX, this.ballVY) + SPEED_PER_RETURN, MAX_SPEED)
    const angle = -Math.PI / 2 + offset * 1.05
    this.ballVX = Math.cos(angle) * speed
    this.ballVY = Math.sin(angle) * speed
    this.ballY = PADDLE_Y
  }

  #hitBricks() {
    const brickWidth = 1 / COLUMNS
    const brickHeight = 0.07
    const top = 0.08

    const column = Math.floor(this.ballX / brickWidth)
    const row = Math.floor((this.ballY - top) / brickHeight)
    if (row < 0 || row >= ROWS || column < 0 || column >= COLUMNS) return

    const brick = this.bricks.find((b) => b.alive && b.row === row && b.column === column)
    if (!brick) return

    brick.alive = false
    this.ballVY *= -1
    this.stage.setScore(this.stage.score + 1)

    if (this.bricks.some((b) => b.alive)) return

    // Mur vidé : on remonte d'une vague, plus rapide, et la vie perdue est pardonnée.
    this.wave += 1
    // Mur vidé : la balle perdue est pardonnée. Sans ça, une bonne partie se finissait sur
    // un capital de vies qui ne remontait jamais.
    this.#setLives(Math.min(LIVES, this.lives + 1))
    this.#buildWall()
    this.#serve()
  }

  #setLives(value) {
    this.lives = value
    this.stage.setLives(value, LIVES)
  }

  #loseLife() {
    this.#setLives(this.lives - 1)
    if (this.lives <= 0) {
      this.over = true
      this.stage.finish({ score: this.stage.score, message: this.labelsValue.dropped || "" })
      return
    }
    this.#serve()
  }

  // --- rendu --------------------------------------------------------------------------

  #draw() {
    if (!this.ctx || !this.bricks) return

    const { ctx, width, height, palette } = this
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = palette.deep(0.10)
    ctx.fillRect(0, 0, width, height)

    const brickWidth = width / COLUMNS
    const brickHeight = height * 0.07
    this.bricks.forEach((brick) => {
      if (!brick.alive) return
      ctx.fillStyle = palette.bricks[brick.row % palette.bricks.length]
      ctx.fillRect(
        brick.column * brickWidth + 2,
        height * 0.08 + brick.row * brickHeight + 2,
        brickWidth - 4,
        brickHeight - 4
      )
    })

    ctx.fillStyle = palette.ink()
    ctx.fillRect((this.paddleX - PADDLE_RATIO / 2) * width, height * 0.93, PADDLE_RATIO * width, height * 0.022)

    ctx.beginPath()
    ctx.fillStyle = palette.hot()
    ctx.arc(this.ballX * width, this.ballY * height, BALL_RATIO * width, 0, Math.PI * 2)
    ctx.fill()
  }
}
